// MF-554 — The Chart Library catalog: search (title, descriptions, labels, sources), filters by group,
// label, kind and data availability, and a gallery of thumbnails drawn with sample data. Built to
// stay usable with hundreds of items: thumbnails render only when scrolled into view, and the list
// grows in pages. Click an item for its detail (LibraryItemDialog).
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import BoardTile from '../BoardTile'
import { sampleData } from '../sample'
import { SUMMARY_PALETTE, TILE } from '../boardTheme'
import ItemChips from './ItemChips'
import LibraryItemDialog from './LibraryItemDialog'
import { matches, tagCounts } from './libraryUtils'

const PAGE = 30
const TOP_TAGS = 24

// The thumbnail draws the real element at THUMB_SCALE of its natural size inside a fixed frame, so the whole element
// fits and nothing spills over the title or the description under it (MF-575).
const THUMB_SCALE = 0.6

/** Renders its children only once scrolled near the viewport (charts are not free), inside a clipped, scaled frame. */
const InView = ({ height, children }) => {
  const ref = useRef(null)
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || seen) return undefined
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setSeen(true), { rootMargin: '200px' })
    io.observe(el)
    return () => io.disconnect()
  }, [seen])
  return (
    <Box
      ref={ref}
      sx={{ height, position: 'relative', overflow: 'hidden', borderRadius: '10px', isolation: 'isolate' }}
    >
      {seen ? (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${100 / THUMB_SCALE}%`,
            height: height / THUMB_SCALE,
            transform: `scale(${THUMB_SCALE})`,
            transformOrigin: 'top left'
          }}
        >
          {children}
        </Box>
      ) : (
        <Skeleton variant='rounded' sx={{ height: '100%', borderRadius: '10px' }} />
      )}
    </Box>
  )
}

const SideTitle = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      color: SUMMARY_PALETTE.faint,
      mt: 2,
      mb: 0.5
    }}
  >
    {children}
  </Typography>
)

const ItemCard = ({ item, onOpen }) => (
  <Box
    role='button'
    tabIndex={0}
    onClick={() => onOpen(item)}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(item)}
    sx={{
      bgcolor: '#fff',
      border: `1px solid ${TILE.border}`,
      borderRadius: TILE.radius,
      p: 1.5,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      opacity: item.compatible ? 1 : 0.7,
      transition: 'box-shadow .15s, border-color .15s',
      '&:hover, &:focus-visible': { borderColor: 'primary.main', boxShadow: '0 4px 14px rgba(20,40,70,.08)' }
    }}
  >
    <InView height={170}>
      <BoardTile
        bare
        tile={{ id: `thumb-${item.code}`, itemCode: item.code, overrides: {} }}
        result={{ loading: false, data: sampleData(item), error: null }}
      />
    </InView>
    <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: SUMMARY_PALETTE.ink, mt: 1 }}>
      {item.title}
    </Typography>
    <Typography
      sx={{
        fontSize: 12,
        color: SUMMARY_PALETTE.muted,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        minHeight: 36
      }}
    >
      {item.description}
    </Typography>
    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <ItemChips item={item} />
      <Typography noWrap sx={{ fontSize: 11, color: SUMMARY_PALETTE.faint, ml: 'auto', minWidth: 0 }}>
        {item.group}
      </Typography>
    </Box>
  </Box>
)

/**
 * @param {{ items: Array|null, onAdd?: Function }} props  onAdd: Board Builder — "Add to board" in the detail.
 */
const LibraryBrowser = ({ items, onAdd }) => {
  const [q, setQ] = useState('')
  const [group, setGroup] = useState(null)
  const [kind, setKind] = useState('all')
  const [tags, setTags] = useState([])
  const [onlyData, setOnlyData] = useState(false)
  const [limit, setLimit] = useState(PAGE)
  const [open, setOpen] = useState(null)

  const all = items || []
  const groups = useMemo(() => {
    const m = new Map()
    for (const i of all) m.set(i.group, (m.get(i.group) || 0) + 1)
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [all])
  const topTags = useMemo(() => tagCounts(all).slice(0, TOP_TAGS), [all])

  const list = useMemo(
    () =>
      all
        .filter(
          (i) =>
            matches(i, q) &&
            (!group || i.group === group) &&
            (kind === 'all' || i.kind === kind) &&
            (!onlyData || i.compatible) &&
            tags.every((t) => (i.tags || []).includes(t))
        )
        .sort((a, b) => Number(b.compatible) - Number(a.compatible) || a.title.localeCompare(b.title)),
    [all, q, group, kind, onlyData, tags]
  )
  useEffect(() => setLimit(PAGE), [q, group, kind, onlyData, tags])

  const toggleTag = (t) => setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]))
  const filtered = Boolean(q || group || kind !== 'all' || onlyData || tags.length)

  return (
    <Box sx={{ display: 'flex', gap: 3, minWidth: 0, alignItems: 'flex-start' }}>
      <Box sx={{ width: 220, flexShrink: 0, position: 'sticky', top: 0 }}>
        <SideTitle>Groups</SideTitle>
        <List dense disablePadding>
          {[[null, all.length], ...groups].map(([g, n]) => (
            <ListItemButton
              key={g || 'all'}
              selected={group === g}
              onClick={() => setGroup(g)}
              sx={{ borderRadius: '8px', py: 0.25 }}
            >
              <ListItemText primary={g || 'All items'} primaryTypographyProps={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.faint }}>{n}</Typography>
            </ListItemButton>
          ))}
        </List>
        <SideTitle>Kind</SideTitle>
        <ToggleButtonGroup
          size='small'
          exclusive
          value={kind}
          onChange={(_, v) => v && setKind(v)}
          sx={{ '& .MuiToggleButton-root': { fontSize: 12, py: 0.25, px: 1.25, textTransform: 'none' } }}
        >
          <ToggleButton value='all'>All</ToggleButton>
          <ToggleButton value='chart'>Charts</ToggleButton>
          <ToggleButton value='card'>Cards</ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={
            <Checkbox size='small' checked={onlyData} onChange={(e) => setOnlyData(e.target.checked)} />
          }
          label={<Typography sx={{ fontSize: 13 }}>Only with data for this school</Typography>}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          fullWidth
          size='small'
          autoFocus
          placeholder='Search by name, description, label or data source'
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <FontAwesomeIcon icon={['fal', 'search']} />
              </InputAdornment>
            )
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
          {topTags.map(([t, n]) => (
            <Chip
              key={t}
              size='small'
              label={`${t} · ${n}`}
              color={tags.includes(t) ? 'primary' : 'default'}
              variant={tags.includes(t) ? 'filled' : 'outlined'}
              onClick={() => toggleTag(t)}
              sx={{ fontSize: 12 }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, flex: 1 }}>
            {items === null ? 'Loading…' : `${list.length} of ${all.length} items`}
          </Typography>
          {filtered ? (
            <Button
              size='small'
              onClick={() => {
                setQ('')
                setGroup(null)
                setKind('all')
                setTags([])
                setOnlyData(false)
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </Box>
        {items === null ? (
          <Skeleton variant='rounded' height={240} />
        ) : list.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, py: 6, textAlign: 'center' }}>
            Nothing matches these filters.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: 2
            }}
          >
            {list.slice(0, limit).map((item) => (
              <ItemCard key={item.code} item={item} onOpen={setOpen} />
            ))}
          </Box>
        )}
        {list.length > limit ? (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button onClick={() => setLimit((l) => l + PAGE)}>Show more ({list.length - limit})</Button>
          </Box>
        ) : null}
      </Box>

      <LibraryItemDialog
        item={open}
        onClose={() => setOpen(null)}
        onAdd={
          onAdd
            ? (item) => {
                setOpen(null)
                onAdd(item)
              }
            : undefined
        }
        onTag={(t) => {
          setOpen(null)
          setTags([t])
        }}
      />
    </Box>
  )
}

export default LibraryBrowser
